import { tryDevLoginBypass } from '../config/devLogin';
import { encryptAuthPassword } from '../lib/authPasswordCrypto';
import { getJson, postJson } from './client';
import type {
  LoginResponse,
  PasswordResetVerifyResponse,
  SignupCompletionResponse,
  SignupNicknameAvailabilityResponse,
  SignupRegistrationResponse,
  SignupTermResponse,
  SignupVerificationVerifyResponse,
  VerificationIssueResponse,
} from '../types/authApi';

export type {
  LoginResponse,
  PasswordResetVerifyResponse,
  SignupCompletionResponse,
  SignupNicknameAvailabilityResponse,
  SignupRegistrationResponse,
  SignupTermResponse,
  SignupVerificationVerifyResponse,
  VerificationIssueResponse,
} from '../types/authApi';

export function requestSignupVerification(email: string) {
  return postJson<VerificationIssueResponse>('/api/v1/auth/signup/request', { email });
}

export function resendSignupVerification(email: string) {
  return postJson<VerificationIssueResponse>('/api/v1/auth/signup/resend', { email });
}

export function verifySignupCode(email: string, verificationCode: string) {
  return postJson<SignupVerificationVerifyResponse>('/api/v1/auth/signup/verify', {
    email,
    verificationCode,
  });
}

export async function registerSignup(
  verifiedEmailToken: string,
  password: string,
  passwordConfirm: string
) {
  const [encryptedPassword, encryptedPasswordConfirm] = await Promise.all([
    encryptAuthPassword(password),
    encryptAuthPassword(passwordConfirm),
  ]);
  return postJson<SignupRegistrationResponse>(
    '/api/v1/auth/signup/registration',
    encryptedPassword && encryptedPasswordConfirm
      ? { encryptedPassword, encryptedPasswordConfirm, verifiedEmailToken }
      : { password, passwordConfirm, verifiedEmailToken }
  );
}

/** 소셜-온리 계정에 이메일/비밀번호 로그인 수단을 추가한다 (세션 인증 필요). */
export async function setLocalCredential(verifiedEmailToken: string, password: string) {
  const encryptedPassword = await encryptAuthPassword(password);
  return postJson<null>(
    '/api/v1/users/me/local-credential',
    encryptedPassword
      ? { encryptedPassword, verifiedEmailToken }
      : { password, verifiedEmailToken }
  );
}

export function getSignupTerms() {
  return getJson<SignupTermResponse[]>('/api/v1/auth/signup/terms');
}

export function getNicknameAvailability(nickname: string) {
  const params = new URLSearchParams({ nickname });
  return getJson<SignupNicknameAvailabilityResponse>(
    `/api/v1/auth/signup/nickname/availability?${params.toString()}`
  );
}

export function completeSignup(
  signupCompletionToken: string,
  nickname: string,
  gender: 'MALE' | 'FEMALE' | 'PREFER_NOT_TO_SAY',
  regionCode: string,
  profileImageRef: string,
  consents: Array<{ agreed: boolean; termCode: string; version: string }>,
  /**
   * JWS ownership ticket from the signup-time profile-image grant.
   * Required by PR-H partial when profileImageRef !== DEFAULT_PROFILE_IMAGE_REF.
   */
  ownershipTicket?: string,
  autoLogin = false
) {
  return postJson<SignupCompletionResponse>('/api/v1/auth/signup/completion', {
    autoLogin,
    consents,
    gender,
    nickname,
    profileImageRef,
    regionCode,
    signupCompletionToken,
    ownershipTicket,
  });
}

export function requestPasswordReset(email: string) {
  return postJson<VerificationIssueResponse>('/api/v1/auth/password/reset/request', { email });
}

export function resendPasswordReset(email: string) {
  return postJson<VerificationIssueResponse>('/api/v1/auth/password/reset/resend', { email });
}

export function verifyPasswordResetCode(email: string, verificationCode: string) {
  return postJson<PasswordResetVerifyResponse>('/api/v1/auth/password/reset/verify', {
    email,
    verificationCode,
  });
}

export async function completePasswordReset(
  passwordResetToken: string,
  password: string,
  passwordConfirm: string
) {
  const [encryptedPassword, encryptedPasswordConfirm] = await Promise.all([
    encryptAuthPassword(password),
    encryptAuthPassword(passwordConfirm),
  ]);
  return postJson<{ userId: string }>(
    '/api/v1/auth/password/reset/confirm',
    encryptedPassword && encryptedPasswordConfirm
      ? { encryptedPassword, encryptedPasswordConfirm, passwordResetToken }
      : { password, passwordConfirm, passwordResetToken }
  );
}

export type ProfileImageUploadResponse = {
  profileImageRef: string;
  uploadUrl: string;
  publicUrl: string;
  expiresAt: string;
  /** JWS ownership ticket — required by completeSignup when image changes (PR-H partial). */
  ownershipTicket?: string;
  uploadHeaders?: Record<string, string>;
};

export function requestProfileImageUpload(
  signupCompletionToken: string,
  fileName: string,
  contentType: string,
  contentLength: number
) {
  return postJson<ProfileImageUploadResponse>('/api/v1/auth/signup/profile-image/upload', {
    signupCompletionToken,
    fileName,
    contentType,
    contentLength,
  });
}

export type LoginDeviceInfo = {
  deviceId: string;
  platform: 'WEB' | 'ANDROID' | 'IOS';
  fcmToken?: string;
  appVersion?: string;
  osVersion?: string;
  deviceModel?: string;
};

export async function login(email: string, password: string, device?: LoginDeviceInfo) {
  const bypass = tryDevLoginBypass(email, password);
  if (bypass) {
    return bypass;
  }

  const encryptedPassword = await encryptAuthPassword(password);
  const credentials = encryptedPassword ? { email, encryptedPassword } : { email, password };
  return postJson<LoginResponse>(
    '/api/v1/auth/login',
    device ? { ...credentials, device } : credentials
  );
}
